import * as React from 'react';
import { useState, useEffect, useCallback } from "react";
import MuiDrawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import FormControl from "@mui/material/FormControl";
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import LinearProgress from '@mui/material/LinearProgress';
import axios from "axios"
import emailjs from "emailjs-com";
import { useDropzone } from "react-dropzone";
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, deleteDoc, collection, onSnapshot, orderBy, limit, query, where, addDoc, updateDoc } from 'firebase/firestore'
import { getStorage, ref, deleteObject, uploadBytes, getDownloadURL } from "firebase/storage";

// Connect to current firebase project
const firebaseApp = initializeApp({
  apiKey: "AIzaSyBamRPS6NxxPg7lplFrxZd36fLWg9ZUTDY",
  authDomain: "ambient-systems.firebaseapp.com",
  databaseURL: "https://ambient-systems-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "ambient-systems",
  storageBucket: "ambient-systems.appspot.com",
  messagingSenderId: "1001740093061",
  appId: "1:1001740093061:web:b516ca06c0e80199b90bec"
});
const firestore = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
      '& .MuiDrawer-paper': {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        boxSizing: 'border-box',
        ...(!open && {
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          width: theme.spacing(7),
          [theme.breakpoints.up('sm')]: {
            width: theme.spacing(9),
          },
        }),
      },
    }),
  );

const drawerWidth = 220;


function createData(date, type, file, id, name, status, method, model) {
  return {
    date, 
    type, 
    file, 
    id, 
    name, 
    status,
    method,
    model
  };
}


// Functions for tables to show data in different way
function descendingComparator(a, b, orderB) {
  if (b[orderB] < a[orderB]) {
    return -1;
  }
  if (b[orderB] > a[orderB]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderB) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderB)
    : (a, b) => -descendingComparator(a, b, orderB);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}


// Attributes of table in first row
const headCells = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'name',
  },
  {
    id: 'id',
    numeric: true,
    disablePadding: false,
    label: 'id',
  },
  {
    id: 'date',
    numeric: true,
    disablePadding: false,
    label: 'date',
  },
  {
    id: 'type',
    numeric: true,
    disablePadding: false,
    label: 'type',
  },
  {
    id: 'file',
    numeric: true,
    disablePadding: false,
    label: 'file name',
  },
  {
    id: 'status',
    numeric: true,
    disablePadding: false,
    label: 'status',
  },
  {
    id: 'source',
    numeric: true,
    disablePadding: false,
    label: 'source',
  },
  {
    id: 'model',
    numeric: true,
    disablePadding: false,
    label: 'model attached',
  }
];

function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderB, numSelected, rowCount, onRequestSort } =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                        'aria-label': 'select all desserts',
                        }}
                    />
                </TableCell>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderB === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderB === headCell.id}
                            direction={orderB === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                        {headCell.label}
                        {orderB === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                        ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
  );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderB: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

// Main function of this page
export default function EngineeringPage() {
    const theme = useTheme()
    const [open, setOpen] = React.useState(false);
    const toggleDrawer = () => {
        setOpen(!open);
    };

    const [connectors, setConnectors] = useState([])
    const [nameToId, setNameToId] = useState([])
    const [nameToFile, setNameToFile] = useState([])
    const [nameToModel, setNameToModel] = useState([])

    /* Identify the current user */
    const user_email = window.localStorage.getItem('user_email');

    /* Firestore SDK queries */
    useEffect(() => {
        const connectorQuery = query(
            collection(firestore, 'connectors'),
            where('user_email', '==', user_email)
        );
        const unsubscribe = onSnapshot(connectorQuery, snapshot => {
            setConnectors(snapshot.docs.map(doc => ({id: doc.id, data: doc.data() })))
            setNameToId(snapshot.docs.map(doc => ({id: doc.id, name: doc.data().name })))
            setNameToFile(snapshot.docs.map(doc => ({file: doc.data().file, name: doc.data().name })))
        })

        return () => {
            unsubscribe()
        }
        }, [])

    useEffect(() => {
        const modelQuery = query(
            collection(firestore, 'feedToModel'),
            where('user_email', '==', user_email)
        );
        const unsubscribe = onSnapshot(modelQuery, snapshot => {
            setNameToModel(snapshot.docs.map(doc => ({id: doc.id, name: doc.data().name })))
        })

        return () => {
            unsubscribe()
        }
        }, [])

    var rows = []
    const test = connectors.map((row) => {
        rows.push(createData(row.data.dataEdit.toDate().toString(), row.data.dataType, row.data.file, row.data.id, row.data.name, row.data.status, row.data.method, String(row.data.models)))
    })

    const [feed, setFeed] = useState("");
    const [fileId, setFileId] = useState("");
    const [fileName, setFileName] = useState("");
    const [feedData, setFeedData] = useState([]);
    const [modelId, setModelId] = useState("");
    const [siteId, setSiteId] = useState("");
    const [site, setSite] = useState("");

    useEffect(() => {
        const connectorQuery = query(
            collection(firestore, 'connectors'),
            where('user_email', '==', user_email),
            where('name', '==', feed)
        );
        const unsubscribe = onSnapshot(connectorQuery, snapshot => {
            setFileId(snapshot.docs.map(doc => doc.id))
            setFileName(snapshot.docs.map(doc => doc.data().file ))
            setFeedData(snapshot.docs.map(doc => doc.data()))
            setSiteId(snapshot.docs.map(doc => doc.data().siteId))
        })

        return () => {
            unsubscribe()
        }
        }, [feed])

    useEffect(() => {
        const modelQuery = query(
            collection(firestore, 'feedToModel'),
            where('user_email', '==', user_email),
            where('name', '==', feed)
        );
        const unsubscribe = onSnapshot(modelQuery, snapshot => {
            setModelId(snapshot.docs.map(doc => doc.id))
        })

        return () => {
            unsubscribe()
        }
        }, [feed])

    useEffect(() => {
        const siteQuery = query(
            collection(firestore, 'sites'),
            where('email', '==', user_email),
            where('siteId', '==', Number(siteId))
        );
        const unsubscribe = onSnapshot(siteQuery, snapshot => {
            setSite(snapshot.docs.map(doc => doc.data()))
        })

        return () => {
            unsubscribe()
        }
    }, [siteId])

    function handleView(e) {
        e.preventDefault()
        if (feed == "") {
            alert("Please choose 1")
            return 
        }

        // modelId.forEach(id => {
        //     deleteDoc(doc(firestore, "feedToModel", id))
        // })

        // deleteDoc(doc(firestore, "connectors", fileId[0]))
        // deleteObject(ref(storage, user_email+'/'+fileName[0]))

        const fname = feedData[0].file
        getDownloadURL(ref(storage, user_email + '/' + fname))
        .then((url) => {
            const xhr = new XMLHttpRequest();
            xhr.responseType = 'blob';
            xhr.onload = (event) => {
            const blob = xhr.response;
            
            const reader = new FileReader()
            reader.onload = function (e) {
            const text = e.target.result;
            const Data = csvToArray(text);
            setTmpdata(Data)
            };
            reader.readAsText(blob)

            setFeedView(true)
            };
            xhr.open('GET', url);
            xhr.send();
        })
        .catch((error) => {
            console.log(error.message)
            alert(error.message)
        });
    }

    const [order, setOrder] = React.useState('asc');
    const [orderB, setOrderB] = React.useState('calories');
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [dense, setDense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    // functions for table
    const handleRequestSort = (event, property) => {
      const isAsc = orderB === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderB(property);
    };

    const handleSelectAllClick = (event) => {
      if (event.target.checked) {
        const newSelecteds = rows.map((n) => n.name);
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    };

    const handleClick = (event, name) => {
      const selectedIndex = selected.indexOf(name);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }

      setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const handleChangeDense = (event) => {
      setDense(event.target.checked);
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
      page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
    
    const EnhancedTableToolbar = (props) => {
        const { numSelected } = props;
    
        return (
            <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                bgcolor: (theme) =>
                    alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
            >
            {numSelected > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    Details
                </Typography>
            )}
    
            {numSelected > 0 ? (
                <Tooltip title="Delete">
                    <IconButton>
                        <ButtonBase onClick={deleteFile}><DeleteIcon /></ButtonBase>
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Filter list">
                    <IconButton>
                        <FilterListIcon />
                    </IconButton>
                </Tooltip>
            )}
            </Toolbar>
      );
    };
    
    EnhancedTableToolbar.propTypes = {
        numSelected: PropTypes.number.isRequired,
    };

    // Delete the feeds in table, also from the database
    function deleteFile(e) {
        e.preventDefault()

        if (window.confirm("Are you sure that you want to delete the feeds you select? They cannot be recovered once deleted.")) {
            var ids = []
            var files = []
            var models = []
            selected.forEach(feed => {
                nameToId.forEach(record => {
                    if (feed == record.name) {
                        ids.push(record.id)
                    }
                })
            });
            selected.forEach(feed => {
                nameToFile.forEach(file => {
                    if (feed == file.name) {
                        files.push(file.file)
                    }
                })
            });
            selected.forEach(feed => {
                nameToModel.forEach(model => {
                    if (feed == model.name) {
                        models.push(model.id)
                    }
                })
            });

            ids.forEach(id => {
                deleteDoc(doc(firestore, "connectors", id))
            })
            files.forEach(file => {
                deleteObject(ref(storage, user_email+'/'+file))
            })
            models.forEach(mid => {
                deleteDoc(doc(firestore, "feedToModel", mid))
            })
            alert("Finished!")
        } else {
            alert("Canceled!")
        }
    }

    const [inputFlow1, setInputFlow1] = useState(false)
    const [inputFlow2, setInputFlow2] = useState(false)
    const [inputFlow3, setInputFlow3] = useState(false)
    const [showData, setShowData] = useState(false)
    const [APIRequest, setAPIRequest] = useState(false)
    const [feedView, setFeedView] = useState(false)
    const [feedModify, setFeedModify] = useState(false)
    const [feedCreate, setFeedCreate] = useState(false)

    const [name, setName] = useState([])
    const [type, setType] = useState([])
    // const [portfolio, setPortfolio] = useState([])
    // const [building, setBuilding] = useState([])
    // const [strata, setStrata] = useState([])

    const [filename, setFilename] = useState([])
    const [source, setSource] = useState({"name": ""})
    const [tmpheader, setTmpheader] = useState([])
    const [tmpdata, setTmpdata] = useState([])
    const [data, setData] = useState([])

    const [link, setLink] = useState([])
    const [server, setServer] = useState([])
    const [databaseName, setDatabaseName] = useState([])
    const [userName, setUserName] = useState([])
    const [password, setPassword] = useState([])

    const [current, setCurrent] = useState([])
    const [database, setDatabase] = useState([])
    const [status, setStatus] = useState([])
    const [api, setApi] = useState([])
    const [apiName, setApiName] = useState([])

    const [modify1, setModify1] = useState([])
    const [modify2, setModify2] = useState([])

    const [ID, setID] = useState();
    useEffect(() => {
        const idQuery = query(
            collection(firestore, 'connectors'),
            where('user_email', '==', user_email),
            limit(1),
            orderBy('id', 'desc')
        );
        onSnapshot(idQuery, snapshot => {
            const t = (snapshot.docs.map(doc => ({ id: doc.data().id })))
            if (!t[0]) {
                setID('0')
            } else {
                setID(JSON.stringify(t[0].id))
            }
        })
    }, [])

    useEffect(() => {
        const databaseQuery = query(
        collection(firestore, 'userDatabase'),
        where('user_email', '==', user_email)
        );
        const unsubscribe = onSnapshot(databaseQuery, snapshot => {
            setDatabase(snapshot.docs.map(doc => ({id: doc.id, data: doc.data() })))
        })

        return () => {
            unsubscribe()
        }
    }, [])

    useEffect(() => {
        const statusQuery = query(
        collection(firestore, 'userDatabase'),
        where('user_email', '==', user_email),
        where('databaseName', '==', current)
        );
        const unsubscribe = onSnapshot(statusQuery, snapshot => {
            setStatus(snapshot.docs.map(doc => doc.data().status ))
            setApi(snapshot.docs.map(doc => doc.data().apiAddress ))
            setApiName(snapshot.docs.map(doc => doc.data().databaseName ))
        })

        return () => {
            unsubscribe()
        }
    }, [current])

    /* Drag and Drop area */
    const onDrop = useCallback((acceptedFiles) => {
        acceptedFiles.map((file) => {
            uploadFiles(file, file.name, "File uploaded")
        });
    }, []);
    const { getRootProps, getInputProps, acceptedFiles, isDragActive } = useDropzone({ onDrop, accept: {'text/csv': ['.csv']} })

    /* Storage upload functions */
    function uploadFiles(file, name, method) {
        console.log(file)
        setFilename(name)
        setSource({"name": name, "method": method})
        const reader = new FileReader()
  
        reader.onload = function (e) {
          const text = e.target.result;
          const Data = csvToArray(text);
          setTmpdata(Data)
        };
  
        reader.readAsText(file)
        
        const userRef = ref(storage, user_email+'/'+name)
        uploadBytes(userRef, file).then((snapshot) => {
            console.log("Uploaded file!")
            alert("Upload successfully!")
            handleClose2()
            handleClose3()
            setShowData(true)

            emailjs.send("teamService","template_cqyfsfh",{
                subject: "User uploaded a new file!",
                name: "File name is " + name,
                email: "from " + user_email,
                message: "Please check in the firebase storage",
            }, "r87tnpnYE2FXwhAzx");

            const email = user_email
            const content = "Uploaded a file : " + name
            const date = formatDate()
            const time = new Date()
            addDoc(collection(firestore, 'alerts'), {email, content, date, time})
        })
    }

    function csvToArray(str, delimiter = ",") {
        const headers = str.slice(0, str.indexOf("\n")).split(delimiter);
        if (headers.length > 10) {
          const res = headers.slice(0, 10)
          setTmpheader(res)
        } else {
          setTmpheader(headers)
        }
        const rows = str.slice(str.indexOf("\n") + 1).split("\n");
        const arr = rows.map(function (row) {
          const values = row.split(delimiter);
          const el = headers.reduce(function (object, header, index) {
            object[header] = values[index];
            return object;
          }, {});
          return el;
        });
  
        if (arr.length > 100) {
          return arr.slice(0, 100)
        } else {
          return arr
        }
        return 
    }

    function formatDate() {
        var d = new Date(),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();
    
        if (month.length < 2) 
            month = '0' + month;
        if (day.length < 2) 
            day = '0' + day;
    
        return [year, month, day].join('-');
    }

    function handleSubmit(e) {
        e.preventDefault()
        if (link == "" || server == "" || databaseName == "" || userName == "" || password == "") {
            alert("Please fill in all fields below!")
            return 
        }

        const CollectionRef = collection(firestore, 'userDatabase')
        const status = "Not connected"
        const apiAddress = ""
        addDoc(CollectionRef, {user_email, link, server, databaseName, userName, password, status, apiAddress})
        .then(response => {
            console.log(response.id)
            alert("Have sent request! Please wait for us to build connection to your data. Check it later.")
            setAPIRequest(false)
            setFeedCreate(true)
            emailjs.send("teamService","template_cqyfsfh",{
                subject: "New Request of API Connection",
                name: server,
                email: user_email,
                message: link,
            }, "r87tnpnYE2FXwhAzx");

            const email = user_email
            const content = "Requested an API connection: " + databaseName
            const date = formatDate()
            const time = new Date()
            addDoc(collection(firestore, 'alerts'), {email, content, date, time})
        })
        .catch(error => {
            console.log(error.message)
            alert(error.message)
            setAPIRequest(false)
            setFeedCreate(true)
        })
    }

    function handleConnectDatabase() {
        axios
            .post(
                api[0],
                {
                    email: user_email
                }
            )
            .then((response) => {
                const res = exportJSONToCSV(response.data)
                uploadFiles(res, apiName+".csv", "API connection")
            }).catch((error) => {
                console.log(error.message)
                alert(error.message)
            });
    }

    function exportJSONToCSV(objArray) {
        var arr = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
        var str =
          `${Object.keys(arr[0])
            .map((value) => `${value}`)
            .join(',')}` + '\r\n';
        var csvContent = arr.reduce((st, next) => {
          st +=
            `${Object.values(next)
              .map((value) => `${value}`)
              .join(',')}` + '\r\n';
          return st;
        }, str);
        var blob = new Blob([csvContent]);
        return blob
    }

    function handleModifySubmit() {
        if (modify1 == '' || modify2 == '') {
            alert("Some value is empty!")
        } else {
            if (window.confirm("Are you sure that you want to modify the information? They will cover the previous records.")) {
                updateDoc(doc(firestore, 'connectors/'+fileId), {name: modify1, dataType: modify2})
                .then(() => {
                    alert("Updated!")
                    closeModify()
                })
                .catch((error) => {
                    console.log(error.message)
                    alert(error.message)
                });
            }
        }
    }

    function handleFeedCreate(e) {
        e.preventDefault()
        if (name == "" || type == "") {
            alert("Please give all the information required below!")
            return 
        }

        const CollectionRef = collection(firestore, 'connectors')
        const dataEdit = new Date()
        const id = Number(ID) + 1
        const dataType = type
        const status = "50%"
        var file = ''
        var method = ''
        if (source.name=="") {
            file = 'Under development..'
            method = 'API connection'
        } else {
            file = source.name
            method = source.method
        }
        const models = []

        addDoc(CollectionRef, {dataEdit, dataType, id, name, status, siteId: "", user_email, file, method, models})
        .then(response => {
            console.log(response.id)
            const fileId = response.id
            alert("Set successfully! Please check it on <Data Feeds> page.")
        })
        .catch(error => {
            console.log(error.message)
            alert(error.message)
        })

        const email = user_email
        const content = "Created a new feed : " + name
        const date = formatDate()
        const time = new Date()
        addDoc(collection(firestore, 'alerts'), {email, content, date, time})
        closeCreate()
    }

    function handleAdd() {
        setInputFlow1(true)
    }

    const handleClose1 = () => {
        setInputFlow1(false)
    }

    const handleClose2 = () => {
        setInputFlow2(false)
    }

    const handleClose3 = () => {
        setInputFlow3(false)
        setCurrent([])
    }

    const closeData = () => {
        setShowData(false)
        setFeedCreate(true)
    }

    const handleHistorical = () => {
        setInputFlow1(false)
        setInputFlow2(true)
    }

    const handleLive = () => {
        setInputFlow1(false)
        setInputFlow3(true)
    }

    const handleConnection = () => {
        setInputFlow3(false)
        setAPIRequest(true)
    }

    const RequestClose = () => {
        setAPIRequest(false)
        setLink([])
        setServer([])
        setDatabaseName([])
        setUserName([])
        setPassword([])
    }

    const closeView = () => {
        setFeedView(false)
    }

    const handleModify = () => {
        if (feed == "") {
            alert("Please choose 1")
            return 
        }

        if (feedData) {
            setModify1(feedData[0].name)
            setModify2(feedData[0].dataType)
        }
        setFeedModify(true)
    }

    const closeModify = () => {
        setFeedModify(false)
        setModify1([])
        setModify2([])
    }

    const closeCreate = () => {
        setFeedCreate(false)
        setName([]);
        setType([]);
        // setPortfolio([]);
        // setBuilding([]);
        // setStrata([]);
    }

    return (  
    <Box sx={{ display: 'flex', height: '100%'}}>

        {/* Side bar layout */}
        <Drawer variant="permanent" open={open} >
            <Toolbar
                sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: [1],
                height: 66,
                }}
            >
                <IconButton onClick={toggleDrawer} sx={{ mr: 1 }}>
                    {open ?<KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
                </IconButton>
            </Toolbar>

            <Divider />

            {/* Navigations to different pages */}
            <List component="nav">
                <ListItemButton href="/Dashboard/Engineering" >
                    <ListItemIcon>
                        <AssignmentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Data Feeds" />
                </ListItemButton>

                <ListItemButton href="/Dashboard/Engineering/Processing" >
                    <ListItemIcon>
                        <AssignmentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Data Processing" />
                </ListItemButton>

                <ListItemButton href="/Dashboard/Engineering/Augmentation">
                    <ListItemIcon>
                        <AssignmentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Data Augmentation" />
                </ListItemButton>
            </List>
        </Drawer>

        <Box sx={{ width: '90%' }}>
            {/* Title */}
            <Typography
                component="h1"
                variant="subtitle1"
                color="inherit"
                noWrap
                sx={{ flexGrow: 1, mt: 3, ml: 8 }}
                >
                Ambient / Data Engineering / Data Feeds
            </Typography>
            <Typography
                component="h1"
                variant="h4"
                color="inherit"
                noWrap
                sx={{ flexGrow: 1, ml: 8, mb: 2 }}
                >
                Data Feeds
            </Typography>

            {/* Select data feeds */}
            <Box sx={{ ml: 3, display: "flex" }}>
                <Typography
                    component="h1"
                    variant="subtitle1"
                    color="inherit"
                    noWrap
                    sx={{ mt: 3, ml: 2 }}
                    >
                    Please select one data feed of yours: 
                </Typography>

                <FormControl sx={{ ml: 1, mt: 2, minWidth: 200 }} variant= "standard">
                    <Select native defaultValue="" label="Feed" disableUnderline
                        theme={theme}
                        sx={{
                            borderRadius: 25,
                            backgroundColor: theme.palette.background.default,
                            padding: "6px 15px",
                        }}
                        value={feed}
                        onChange={(e) => {setFeed(e.target.value)}}>
                        
                        <option aria-label="None" value="">Your feed</option>
                        {connectors?.map((row) => (
                            <option value={row.data.name}>{row.data.name}</option>
                        ))}
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2, ml: 2, borderRadius: 5 }}
                    position="absolute"
                    onClick={handleView}
                >
                    View
                </Button>

                <Button
                    variant="contained"
                    color="warning"
                    sx={{ mt: 2, ml: 2, borderRadius: 5 }}
                    position="absolute"
                    onClick={handleModify}
                >
                    Modify
                </Button>

                <Typography
                    component="h1"
                    variant="subtitle1"
                    color="#E97451"
                    noWrap
                    sx={{ mt: 3, ml: 10 }}
                    >
                    Add a new feed:
                </Typography>

                <Button
                    variant="contained"
                    color="secondary"
                    sx={{ mt: 2, ml: 3, borderRadius: 5 }}
                    position="absolute"
                    onClick={handleAdd}
                >
                    Create
                </Button>
            </Box>

            <Dialog open={inputFlow1} onClose={handleClose1} maxWidth={"sm"} fullWidth={true}>
                <DialogTitle>STEP 1</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select your data source:
                    </DialogContentText>
                    
                    <Box sx={{ display: 'flex', minHeight: 200, mt: 3, p: 2 }}>
                        <ButtonBase sx={{ minWidth: '45%' }} onClick={handleHistorical}>
                            <Box display="flex" justifyContent="center" alignItems="center">
                                Historical File
                            </Box>
                        </ButtonBase>
                        <Box sx={{ ml: 2, mr: 2, border: 1, borderColor: '#B0C4DE' }} />
                        <ButtonBase sx={{ minWidth: '50%' }} onClick={handleLive}>
                            <Box display="flex" justifyContent="center" alignItems="center">
                                Live API Connection
                            </Box>
                        </ButtonBase>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose1}>Cancel</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={inputFlow2} onClose={handleClose2} maxWidth={"md"} fullWidth={true}>
                <DialogTitle>STEP 2</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please upload your file here
                    </DialogContentText>
                    
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ border: 2, borderStyle: 'dotted' }}>
                            <Box {...getRootProps({})} display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 400 }}>
                                <input {...getInputProps()} onDrop={onDrop}/>
                                {isDragActive ? (
                                    <Typography variant="h5">Drop (only .csv file)</Typography>
                                ) : (
                                    <Typography variant="h5">Drag file and drop here / Click to upload</Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose2}>Cancel</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={inputFlow3} onClose={handleClose3} maxWidth={"md"} fullWidth={true}>
                <DialogTitle>STEP 2</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Request your own API connection or check the status of your previous requests
                    </DialogContentText>
                    
                    <Box>
                        <Box sx={{ display: 'flex', mt: 3 }}>
                            <Typography variant="h7" sx={{ mt: 2 }}>Create a new request of API Connection:</Typography>
                            <Button variant="contained" color='primary' sx={{ mt: 1, ml: 3 }} onClick={handleConnection}>Connect your own DB / API</Button>
                        </Box>
                        <Box sx={{ mt: 3, border: 1, borderColor: '#B0C4DE' }} />
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h7">Check the previous connections you request:</Typography>
                            <FormControl variant= "standard" sx={{ mt: -1, ml: 3 }}>
                                <Select native defaultValue="" id="grouped-native-select" label="Select Connection" disableUnderline onChange={(e) => setCurrent(e.target.value)}
                                    theme={theme}
                                    sx={{
                                        borderRadius: 25,
                                        backgroundColor: theme.palette.background.default,
                                        padding: "6px 15px",
                                    }}
                                >
                                    <option value="">Select Connection</option>
                                    {database?.map((row) => (
                                    <option value={row.data.databaseName}>{row.data.databaseName}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ mt: 4 }}>
                            {current? (
                                <Typography variant="h7" color="#228B22">Status: {status[0]}</Typography>
                            ):(
                                <div></div>
                            )}
                            {status[0] == "Connected" ? (
                                <Button sx={{ ml : 5, mt: -0.3 }} onClick={handleConnectDatabase}>
                                    import data
                                </Button>
                            ):(
                                <div></div>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose3}>Cancel</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={APIRequest} onClose={RequestClose}>
                <DialogTitle>External Connection</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please provide the information for us to connect and get the data.
                    </DialogContentText>

                    <FormControl sx={{ mt: 2, mb: 2, minWidth: 500 }}>
                        <TextField autoFocus margin="dense" id="server" label="Type of database (e.g. GCP SQL / Own server, Postgre)" fullWidth variant="standard" onChange={(e) => {setServer(e.target.value)}} />
                        <TextField autoFocus margin="dense" id="link" label="Link (e.g. endpoint ID number / URL )" fullWidth variant="standard" onChange={(e) => {setLink(e.target.value)}} />
                        <TextField autoFocus margin="dense" id="databaseName" label="Database name" fullWidth variant="standard" onChange={(e) => {setDatabaseName(e.target.value)}} />
                        <TextField autoFocus margin="dense" id="userName" label="Username of database account" fullWidth variant="standard" onChange={(e) => {setUserName(e.target.value)}} />
                        <TextField autoFocus margin="dense" id="password" label="Password of database user" fullWidth variant="standard" onChange={(e) => {setPassword(e.target.value)}} />
                    </FormControl>
                </DialogContent>

                <DialogActions>
                    <Button onClick={RequestClose}>Cancel</Button>
                    <Button onClick={handleSubmit}>Submit</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showData} maxWidth={"lg"} fullWidth={true}>
                <DialogTitle>Data Check</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Can check part content of your file below
                    </DialogContentText>
                    
                    <Box>
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table
                            sx={{ minWidth: 650 }}
                            size="small"
                            aria-label="a dense table"
                            >
                            <TableHead>
                                <TableRow>
                                {tmpheader?.map((column) => (
                                    <TableCell align="right">{column}</TableCell>
                                ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {tmpdata?.map((row) => (
                                <TableRow
                                    key={Object.values(row)[0]}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row">
                                    {Object.values(row)[0]}
                                    </TableCell>
                                    <TableCell align="right">{Object.values(row)[1]}</TableCell>
                                    <TableCell align="right">{Object.values(row)[2]}</TableCell>
                                    <TableCell align="right">{Object.values(row)[3]}</TableCell>
                                    <TableCell align="right">{Object.values(row)[4]}</TableCell>
                                    <TableCell align="right">{Object.values(row)[5]}</TableCell>
                                    <TableCell align="right">{Object.values(row)[6]}</TableCell>
                                    <TableCell align="right">{Object.values(row)[7]}</TableCell>
                                    <TableCell align="right">{Object.values(row)[8]}</TableCell>
                                    <TableCell align="right">{Object.values(row)[9]}</TableCell>
                                </TableRow>
                                ))}
                            </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeData}>Set up Feed</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={feedView} onClose={closeView} maxWidth={"lg"} fullWidth={true}>
                <DialogTitle>Information View</DialogTitle>
                <DialogContent>
                    <Box>
                        <FormControl sx={{ ml: 2 }}>
                            {feedData?.map((row) => (
                            <div>
                                <Typography variant="h7">Basic Info</Typography>
                                <Box sx={{ mt: 1 }}>
                                    <TextField label="No." variant="standard" InputProps={{ readOnly: true }} defaultValue={row.id} />
                                    <TextField label="Name" variant="standard" InputProps={{ readOnly: true }} defaultValue={row.name} sx={{ ml: 4 }}/>
                                    <TextField label="Type" variant="standard" InputProps={{ readOnly: true }} defaultValue={row.dataType} sx={{ ml: 4 }}/>
                                    <TextField label="Creation Time" variant="standard" InputProps={{ readOnly: true }} defaultValue={new Date(row.dataEdit * 1000)} sx={{ ml: 4 }}/>
                                    <TextField label="Creation Method" variant="standard" InputProps={{ readOnly: true }} defaultValue={row.method} sx={{ ml: 4 }}/>
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="h7" >Status: {row.status}</Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <LinearProgress variant="determinate" value={parseInt(row.status.substr(0, row.status.length - 1))} />
                                    </Box>
                                </Box>

                                {site?.map((rows) => (
                                    <Box sx={{ mt: 2 }}>
                                    <Typography variant="h7">Others</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <TextField label="Portfolio" variant="standard" InputProps={{ readOnly: true }} defaultValue={rows.portfolio} />
                                        <TextField label="Building" variant="standard" InputProps={{ readOnly: true }} defaultValue={rows.building} sx={{ ml: 4 }}/>
                                        <TextField label="Strata" variant="standard" InputProps={{ readOnly: true }} defaultValue={rows.strata} sx={{ ml: 4 }}/>
                                    </Box>
                                </Box>
                                ))}

                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="h7">Models attached: {row.models}</Typography>
                                </Box>

                                <Box sx={{ mt: 2}}>
                                <Typography variant="h7">File:</Typography>
                                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                                        <Table
                                        sx={{ width: '100%' }}
                                        size="small"
                                        aria-label="a dense table"
                                        >
                                        <TableHead>
                                            <TableRow>
                                            {tmpheader.slice(0, 5)?.map((column) => (
                                                <TableCell align="right">{column}</TableCell>
                                            ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {tmpdata.slice(0, 5)?.map((row) => (
                                            <TableRow
                                                key={Object.values(row)[0]}
                                                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                            >
                                                <TableCell component="th" scope="row">
                                                {Object.values(row)[0]}
                                                </TableCell>
                                                <TableCell align="right">{Object.values(row)[1]}</TableCell>
                                                <TableCell align="right">{Object.values(row)[2]}</TableCell>
                                                <TableCell align="right">{Object.values(row)[3]}</TableCell>
                                                <TableCell align="right">{Object.values(row)[4]}</TableCell>
                                            </TableRow>
                                            ))}
                                        </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </div>
                            ))}
                        </FormControl>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeView}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={feedModify} onClose={closeModify} maxWidth={"md"} fullWidth={true}>
                <DialogTitle>Feed Modification</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Can quickly modify some basic info of your feed here.
                    </DialogContentText>

                    <Box>
                        <FormControl sx={{ ml: 5, mt: 3, mb: 3 }}>
                            {feedData?.map((row) => (
                                <Box sx={{ mt: 1 }}>
                                    <TextField label="Name" variant="standard" defaultValue={row.name} onChange={(e) => {setModify1(e.target.value)}}/>
                                    <TextField label="Type" variant="standard" defaultValue={row.dataType} onChange={(e) => {setModify2(e.target.value)}} sx={{ ml: 5 }}/>
                                </Box>
                            ))}             
                        </FormControl>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleModifySubmit}>Submit</Button>
                    <Button onClick={closeModify}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={feedCreate} onClose={closeCreate}>
                <DialogTitle>Custom Data</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please fill in these fields to label your data.
                    </DialogContentText>

                    <FormControl sx={{ mt: 2, mb: 2, minWidth: 500 }}>
                        <TextField autoFocus margin="dense" id="name" label="Name this feed" fullWidth variant="standard" onChange={(e) => {setName(e.target.value)}} />
                        <TextField autoFocus margin="dense" id="type" label="Data type" fullWidth variant="standard" onChange={(e) => {setType(e.target.value)}} />
                        {/* <TextField autoFocus margin="dense" id="portfolio" label="Portfolio it is related to" fullWidth variant="standard" onChange={(e) => {setPortfolio(e.target.value)}} />
                        <TextField autoFocus margin="dense" id="building" label="Building it is for" fullWidth variant="standard" onChange={(e) => {setBuilding(e.target.value)}} />
                        <TextField autoFocus margin="dense" id="strata" label="Strata" fullWidth variant="standard" onChange={(e) => {setStrata(e.target.value)}} /> */}
                    </FormControl>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeCreate}>Cancel</Button>
                    <Button onClick={handleFeedCreate}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Main body of table */}
            <Box sx={{ p: 3 }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <EnhancedTableToolbar numSelected={selected.length} />
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={dense ? 'small' : 'medium'}
                        >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderB={orderB}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                            <TableBody>
                            {stableSort(rows, getComparator(order, orderB))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                const isItemSelected = isSelected(row.name);
                                const labelId = `enhanced-table-checkbox-${index}`;

                                return (
                                    <TableRow
                                        hover
                                        onClick={(event) => handleClick(event, row.name)}
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.name}
                                        selected={isItemSelected}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                checked={isItemSelected}
                                                inputProps={{
                                                    'aria-labelledby': labelId,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            component="th"
                                            id={labelId}
                                            scope="row"
                                            padding="none"
                                        >
                                            {row.name}
                                        </TableCell>
                                        <TableCell align="right">{row.id}</TableCell>
                                        <TableCell align="right">{row.date}</TableCell>
                                        <TableCell align="right">{row.type}</TableCell>
                                        <TableCell align="right">{row.file}</TableCell>
                                        <TableCell align="right">{row.status}</TableCell>
                                        <TableCell align="right">{row.method}</TableCell>
                                        <TableCell align="right">{row.model}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: (dense ? 33 : 53) * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>

                {/* Switch */}
                <FormControlLabel
                    control={<Switch checked={dense} onChange={handleChangeDense} />}
                    label="Dense padding"
                />
            </Box>
        </Box>
    </Box>
    );
}